import { call, fork, put, select, take, takeEvery } from 'typed-redux-saga/macro';
import { channel } from 'redux-saga';

import actions from './actions';

import type { BeforeInstallPromptEvent, IModalShownState } from './types';
import { setCurrentRoute } from 'features/Auth/slice';
import { getCurrentRoute } from 'features/Auth/selectors';
import { ROUTES } from 'navigation/constants';
import { isIOS, isDev } from 'utils/constants';
import { matchPath } from 'utils/helpers';
import { isCapacitorPlatform } from 'utils/platform';

import { getTier, getId } from '../selectors';
import { UserSubscriptionTier } from '../types';
import { setSubscription } from '../slice';
import selectors from './selectors';

const STORAGE_KEY = 'progressiveWebAppShownState';

function* setShownStateToStorage() {
  const shownState = yield* select(selectors.getShownState);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(shownState));
}

function* getShownStateFromStorage() {
  const storedState = localStorage.getItem(STORAGE_KEY);
  const { timesShown, lastShown }: IModalShownState = storedState
    ? JSON.parse(storedState)
    : { timesShown: 0, lastShown: null };

  yield* put(actions.setTimesShown(timesShown));
  yield* put(actions.setLastShown(lastShown));
}

function* watchPromptOpen() {
  yield* takeEvery(actions.setModalStep, function* ({ payload: step }) {
    if (step === 'prompt') {
      yield* put(actions.setLastShown(Date.now()));
      yield* call(setShownStateToStorage);
    }
  });
}

function* incrementTimesShown() {
  const timesShown = yield* select(selectors.getTimesShown);
  yield* put(actions.setTimesShown(timesShown + 1));
}

function* triggerByRouteChange() {
  do {
    let tier = yield* select(getTier);

    if (!tier) {
      yield* take(setSubscription);
      tier = yield* select(getTier);
    }

    const currentRoute = yield* select(getCurrentRoute);

    const enabledRoutes = [ROUTES.HOME.path, ROUTES.PROFILE.path, ROUTES.LESSON.path];
    const enabledTiers: UserSubscriptionTier[] = ['subscriber', 'former_subscriber'];

    const installable =
      tier &&
      enabledTiers.includes(tier) &&
      enabledRoutes.some(path => matchPath(path, currentRoute));

    yield* put(actions.setInstallable(!!installable));

    const canPrompt = yield* select(selectors.canPrompt);

    // Disable PWA prompt in development environment
    if (installable && canPrompt && !isDev) {
      yield* call(incrementTimesShown);
      yield* put(actions.setModalStep('prompt'));
    }

    yield* take(setCurrentRoute);
  } while (true);
}

function* interactivePrompt() {
  const installableChannel = yield* call(
    channel<{ type: 'installable'; payload: BeforeInstallPromptEvent }>
  );

  const handleBeforeInstallPrompt = (event: BeforeInstallPromptEvent) => {
    event.preventDefault();

    installableChannel.put({ type: 'installable', payload: event });
  };

  yield* call([window, window.addEventListener], 'beforeinstallprompt', handleBeforeInstallPrompt);

  while (true) {
    const { payload: installEvent } = yield* take(installableChannel);

    yield* fork(triggerByRouteChange);

    yield* take(actions.promptProgressiveWebAppInstallation);

    yield* call([installEvent, installEvent.prompt]);

    try {
      yield* call([installEvent, installEvent.userChoice]);
      yield* put(actions.setModalStep(null));
      yield* call(
        [window, window.removeEventListener],
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );

      break;
    } catch {}
  }
}

function* showAppStorePrompt() {
  // Wait for user to complete registration and reach a key route
  let tier = yield* select(getTier);

  if (!tier) {
    yield* take(setSubscription);
    tier = yield* select(getTier);
  }

  // Show to all registered users (any tier)
  if (!tier) {
    return;
  }

  // Wait for navigation to a post-registration route
  while (true) {
    const currentRoute = yield* select(getCurrentRoute);
    const postRegistrationRoutes = ['/', ROUTES.HOME.path, ROUTES.PROFILE.path, ROUTES.LESSON.path];
    
    if (postRegistrationRoutes.some(path => matchPath(path, currentRoute))) {
      break;
    }
    
    yield* take(setCurrentRoute);
  }

  // Check if we can still prompt (under 5 times shown)
  const canPrompt = yield* select(selectors.canPrompt);
  if (!canPrompt) {
    return;
  }

  // Show the app store prompt
  yield* put(actions.setInstallable(true));
  yield* call(incrementTimesShown);
  yield* put(actions.setModalStep('prompt'));
}

export function* watchProgressiveWebApp() {
  // Never show on Capacitor platforms (native apps)
  if (isCapacitorPlatform()) {
    return;
  }

  // Skip in development if needed for testing
  if (isDev) {
    // Can be enabled for testing by removing this return
    // return;
  }

  try {
    yield* call(getShownStateFromStorage);
    yield* fork(watchPromptOpen);
    
    // Show app store prompt instead of PWA prompt
    yield* fork(showAppStorePrompt);
  } catch {}
}
